import { InputHTMLAttributes, ReactNode } from "react";
import { useController, useFormContext } from "react-hook-form";
import { CSSProp } from "styled-components";
import tw from "twin.macro";

const containerCss = tw`h-12 bg-gray border-none rounded-[9px]`;

interface ComponentProps {
  name: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  classes?: {
    root?: CSSProp;
    input?: CSSProp;
  };
}

export type InputProps = ComponentProps & InputHTMLAttributes<HTMLInputElement>;

/**
 * @param name [string] required
 * @param startAdornment [JSX.Element] optional
 * @param endAdornment [JSX.Element] optional
 */
export const Input = ({
  name,
  disabled,
  startAdornment,
  endAdornment,
  className,
  classes,
  ...rest
}: InputProps) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ control, name });

  return (
    <>
      <div
        tw="flex w-full items-center"
        css={[
          containerCss,
          classes?.root,
          error && tw`border border-solid border-orange`,
          className,
        ]}
      >
        {startAdornment && <div tw="pl-4">{startAdornment}</div>}
        <input
          tw="flex-1 px-4 font-normal text-xs focus:outline-none"
          css={[
            containerCss,
            classes?.input,
            error &&
              tw`border-t border-b border-solid border-orange border-l-0`,
            error && endAdornment && tw`rounded-r-none`,
            disabled && tw`text-secondary cursor-not-allowed`,
            className,
          ]}
          disabled={disabled}
          {...field}
          {...rest}
        />
        {endAdornment && <div tw="px-4">{endAdornment}</div>}
      </div>
      {error && (
        <span tw="font-light text-[8px] leading-[14px] text-orange mt-2 self-start">
          {error.message}
        </span>
      )}
    </>
  );
};
